import React, { FC, useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
// import Pagination from "shared/Pagination/Pagination";
// import ButtonPrimary from "shared/Button/ButtonPrimary";
import ProductCard from "components/ProductCard";
import TabFilters from "./TabFilters";
import PaginationPage from "shared/Pagination/PaginationPage";
import { useItemMasterData } from "api/inventory/useItemMasterData";
import { useProductsByCategory } from "api/inventory/useProductsByCategory";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import useCategoryData from "api/category/useCategoryData";
import { getCategoryName, getCategoryNameById } from "utils/categoryMapping";

export interface OurProductCollectionProps {
  className?: string;
}

const OurProductCollection: FC<OurProductCollectionProps> = ({
  className = "",
}) => {
  const [page, setPage] = useState(1);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const titleRef = useRef(null);
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const { CategoryData } = useCategoryData();
  const navigate = useNavigate();


  const payLoad = {
    page: page,
  };
  
  // Fetch all products
  const { ItemMasterData, isLoading: isLoadingItemMaster } =
    useItemMasterData(payLoad);
  const { data: allProductsData, meta: allProductsMeta } = ItemMasterData ?? [];
  
  // Fetch products by category
  const { ProductsByCategoryData, isLoading: isLoadingCategoryProducts } =
    useProductsByCategory(selectedCategoryIds, page);
  const { data: categoryProductsData, meta: categoryProductsMeta } = ProductsByCategoryData ?? [];
  
  // Use category products if categories are selected, otherwise use all products
  const data = selectedCategoryIds.length > 0 ? categoryProductsData : allProductsData;
  const meta = selectedCategoryIds.length > 0 ? categoryProductsMeta : allProductsMeta;
  
  const lastPage = meta?.last_page ?? "";

  // Set selected categories from URL parameters
  useEffect(() => {
    const categoriesParam = searchParams.get('categories');
    
    if (categoryId) {
      // Single category from path parameter
      setSelectedCategoryIds([categoryId]);
      setPage(1);
    } else if (categoriesParam) {
      // Multiple categories from query parameter
      const categoryIds = categoriesParam.split(',').filter(id => id.trim() !== '');
      setSelectedCategoryIds(categoryIds);
      setPage(1);
    } else {
      // No categories selected
      setSelectedCategoryIds([]);
    }
  }, [categoryId, searchParams]);
  const handelSelectedPage = (value: any) => {
    // console.log(value, "values");
    if (titleRef.current && value) {
      const customScrollHeight = (titleRef?.current as HTMLElement)?.offsetTop;
      window.scrollTo({ top: customScrollHeight, behavior: "smooth" });
    }

    setPage(value);
  };

  const handleNext = () => {
    // console.log("EE");
    if (titleRef.current) {
      const customScrollHeight = (titleRef?.current as HTMLElement)?.offsetTop;
      window.scrollTo({ top: customScrollHeight, behavior: "smooth" });
    }

    setPage(() => page + 1);
    if (page >= lastPage) {
      setPage(1);
    }
  };

  const handlePrev = () => {
    if (titleRef.current) {
      const customScrollHeight = (titleRef?.current as HTMLElement)?.offsetTop;
      window.scrollTo({ top: customScrollHeight, behavior: "smooth" });
    }

    setPage(() => page - 1);
    if (page <= 1) {
      setPage(1);
    }
  };
  // const pages = Array.from({ length: lastPage }, (_, index) => index + 1);


  return (
    <div
      className={`nc-PageCollection ${className}`}
      data-nc-id="PageCollection"
    >
      <Helmet>
        <title>Our Product </title>
      </Helmet>

      <div className="container py-5 lg:pb-10 lg:pt-10 ">
        <div className="space-y-5 lg:space-y-5">
          {/* HEADING */}
          <div className="max-w-screen-sm" ref={titleRef}>
            <h2 className="block text-2xl sm:text-3xl lg:text-4xl font-semibold">
              Our Product
            </h2>
          </div>

          <hr className="border-slate-200 dark:border-slate-700" />
          <main>
            <TabFilters 
              selectedCategoryIds={selectedCategoryIds}
              onCategoryChange={setSelectedCategoryIds}
            />

            {/* Selected Categories Display */}
            {selectedCategoryIds.length > 0 && (
              <div className="mt-4 mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Selected Categories:
                  </span>
                  {selectedCategoryIds.map((categoryId) => {
                    const category = CategoryData?.data?.find((cat: any) => {
                      const match = String(cat.id) === String(categoryId);
                      return match;
                    });
                    
                    const categoryName = category ? getCategoryName(category) : getCategoryNameById(categoryId);
                    
                    return (
                      <div
                        key={categoryId}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-sm"
                      >
                        <span>{categoryName}</span>
                        <button
                          onClick={() => {
                            const newCategories = selectedCategoryIds.filter(id => id !== categoryId);
                            setSelectedCategoryIds(newCategories);
                            if (newCategories.length === 0) {
                              navigate('/our-product');
                            } else if (newCategories.length === 1) {
                              navigate(`/our-product/${newCategories[0]}`);
                            } else {
                              const categoryParams = newCategories.join(',');
                              navigate(`/our-product?categories=${categoryParams}`);
                            }
                          }}
                          className="ml-1 hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => {
                      setSelectedCategoryIds([]);
                      navigate('/our-product');
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* LOOP ITEMS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-10 mt-8 lg:mt-10">
              {(selectedCategoryIds.length > 0 ? isLoadingCategoryProducts : isLoadingItemMaster) &&
                Array.from({ length: 12 }).map((_, index) => (
                  <div key={index} className="flex flex-col gap-4 w-52">
                    <div className="skeleton h-52 w-full"></div>
                    <div className="skeleton h-4 w-full"></div>
                    <div className="skeleton h-4 w-28"></div>
                  </div>
                ))}
            </div>
            {/* Products Grid */}
            {data && data.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-10 mt-8 lg:mt-10">
                {data.map((item: any, index: any) => (
                  <ProductCard data={item} key={index} />
                ))}
              </div>
            ) : !(selectedCategoryIds.length > 0 ? isLoadingCategoryProducts : isLoadingItemMaster) ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4">
                  <svg
                    className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {selectedCategoryIds.length > 0 
                    ? "No products available in this category"
                    : "No products found"
                  }
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {selectedCategoryIds.length > 0 
                    ? "Try selecting a different category or browse all products."
                    : "Please try adjusting your filters or check back later."
                  }
                </p>
                {selectedCategoryIds.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedCategoryIds([]);
                      navigate('/our-product');
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  >
                    View All Products
                  </button>
                )}
              </div>
            ) : null}

            <div className="flex flex-col mt-12 lg:mt-16 space-y-5 sm:space-y-0 sm:space-x-3 sm:flex-row sm:justify-center sm:items-center">
              {lastPage > 1 && (
                <PaginationPage
                  totalPages={lastPage}
                  currentPage={page}
                  handlePrev={handlePrev}
                  handleNext={handleNext}
                  handleSelectedPage={handelSelectedPage}
                />
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default OurProductCollection;
